import { useParams } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common/ui/tabs'
import useQueryGetPlayerDetail from '../../api/hooks/useQueryGetPlayerDetail'
import Heading from '@/common/shared/heading'
import { Separator } from '@/common/ui/separator'
import PlayerInfo from './components/PlayerInfo'
import PlayerCumulative from './components/PlayerCumulative'

import { useQueryState } from 'nuqs'
import Spinner from '@/common/components/Spinner'
import { Button } from '@/common/ui/button'
import { UserX, Receipt, GamepadIcon, History, Gamepad2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import TransactionTable from './components/TransactionTable'
import PlaylogTable from './components/PlaylogTable'
import ReferredPlayer from './components/ReferredPlayer'
import PlayerClearTurnoverHistoryTable from './components/PlayerClearTurnoverHistoryTable'
import NavigateBackButton from '@/common/components/NavigateBackButton'
import { PlayerActivityLogTable } from './components/PlayerActivityLogTable'
import PlayerDepositWithdrawGraph from '@/modules/dashboard/components/Dashboard/components/PlayerDepositWithdrawGraph'
import ExpandableReferredPlayersTable from './components/ExpandableReferredPlayersTable'
import InviteFriendTierCounts from './components/InviteFriendTierCounts'
import PlayerProviderStats from './components/PlayerProviderStats'
import PlayerGameStatistics from './components/PlayerGameStatistics'

export default function PlayerDetail() {
	const { id } = useParams<{ id: string }>()
	const [, setPage] = useQueryState('page')
	const [, setLimit] = useQueryState('limit')
	const navigate = useNavigate()

	const { data: playerData, isLoading: isPlayerLoading } =
		useQueryGetPlayerDetail(id || '')

	const handleTabChange = () => {
		setPage('1')
		setLimit('10')
	}

	if (isPlayerLoading) {
		return <Spinner />
	}

	if (!playerData) {
		return (
			<div className="flex h-[50vh] flex-col items-center justify-center gap-4 px-4">
				<div className="rounded-full bg-muted p-4">
					<UserX className="h-12 w-12 text-muted-foreground" />
				</div>
				<div className="text-center">
					<h3 className="text-lg font-semibold">ไม่พบข้อมูลผู้เล่น</h3>
					<p className="text-sm text-muted-foreground">ไม่พบข้อมูลผู้เล่น</p>
				</div>
				<Button onClick={() => navigate(-1)} variant="outline">
					ย้อนกลับ
				</Button>
			</div>
		)
	}

	return (
		<div className="space-y-4 px-4 sm:space-y-8 sm:px-0">
			{/* Header - Mobile optimized */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
				<div className="min-w-0 flex-1">
					<Heading
						title={`ข้อมูลสมาชิก - ${playerData.username || playerData.phone}`}
						description="ข้อมูลสมาชิก"
					/>
				</div>
				<div className="flex-shrink-0">
					<NavigateBackButton />
				</div>
			</div>
			<Separator className="my-4 sm:my-6" />

			{/* Player Info Card */}
			<div className="rounded-lg border bg-card p-4 shadow-sm sm:p-6">
				<PlayerInfo data={playerData} />
			</div>

			{/* Player Cumulative Card */}
			<div className="rounded-lg border bg-card p-4 shadow-sm sm:p-6">
				<PlayerCumulative playerId={id || ''} data={playerData} />
			</div>

			{/* Game Statistics Card */}
			<div className="rounded-lg border bg-card p-4 shadow-sm sm:p-6">
				<div className="mb-4 sm:mb-6">
					<h2 className="text-xl font-bold tracking-tight sm:text-2xl">
						สถิติการเล่นเกม
					</h2>
					<p className="text-sm text-muted-foreground sm:text-base">
						สรุปข้อมูลการเล่นเกมทั้งหมดของสมาชิก
					</p>
				</div>
				<PlayerGameStatistics data={playerData} />
			</div>

			{/* Graph Card */}
			<div className="rounded-lg border bg-card p-4 shadow-sm sm:p-6">
				<PlayerDepositWithdrawGraph playerId={id || ''} />
			</div>

			{/* Invite Friend Tier Counts Card */}
			<div className="rounded-lg border bg-card p-4 shadow-sm sm:p-6">
				<div className="mb-4 sm:mb-6">
					<h2 className="text-xl font-bold tracking-tight sm:text-2xl">
						จำนวนสมาชิกที่แนะนำ
					</h2>
					<p className="text-sm text-muted-foreground sm:text-base">
						จำนวนสมาชิกที่แนะนำในแต่ละลำกับขั้น
					</p>
				</div>
				<InviteFriendTierCounts playerId={id || ''} />
			</div>

			{/* Expandable Referred Players Table Card */}
			<div className="rounded-lg border bg-card p-4 shadow-sm sm:p-6">
				<div className="mb-4 sm:mb-6">
					<h2 className="text-xl font-bold tracking-tight sm:text-2xl">
						สมาชิกที่แนะนำ
					</h2>
					<p className="text-sm text-muted-foreground sm:text-base">
						คลิกที่แถวเพื่อดูสมาชิกที่แนะนำของแต่ละคน
					</p>
				</div>
				<div className="overflow-x-auto">
					<ExpandableReferredPlayersTable
						playerId={id || ''}
						headPlayerId={id || ''}
					/>
				</div>
			</div>

			{/* History Tabs Card */}
			<div className="rounded-lg border bg-card p-4 shadow-sm sm:p-6">
				<div className="mb-4 sm:mb-6">
					<h2 className="text-xl font-bold tracking-tight sm:text-2xl">
						ข้อมูลประวัติ
					</h2>
					<p className="text-sm text-muted-foreground sm:text-base">
						ประวัติการทำธุรกรรมและการเล่นทั้งหมด
					</p>
				</div>

				<Tabs
					defaultValue="transactions"
					className="w-full"
					onValueChange={handleTabChange}
				>
					{/* Mobile-optimized tabs with horizontal scroll */}
					<div className="w-full overflow-x-auto">
						<TabsList className="inline-flex h-12 w-max items-center justify-start rounded-lg bg-muted p-1 text-muted-foreground sm:h-14 sm:w-full sm:justify-between">
							<TabsTrigger
								value="transactions"
								className="inline-flex min-w-max items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm sm:px-6 sm:py-3 sm:text-base"
							>
								<Receipt className="mr-1 h-4 w-4 sm:mr-2 sm:h-5 sm:w-5" />
								<span className="hidden sm:inline">ประวัติการทำธุรกรรม</span>
								<span className="sm:hidden">ธุรกรรม</span>
							</TabsTrigger>
							<TabsTrigger
								value="playlogs"
								className="inline-flex min-w-max items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm sm:px-6 sm:py-3 sm:text-base"
							>
								<GamepadIcon className="mr-1 h-4 w-4 sm:mr-2 sm:h-5 sm:w-5" />
								<span className="hidden sm:inline">ประวัติการเล่น</span>
								<span className="sm:hidden">การเล่น</span>
							</TabsTrigger>
							<TabsTrigger
								value="providerStats"
								className="inline-flex min-w-max items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm sm:px-6 sm:py-3 sm:text-base"
							>
								<Gamepad2 className="mr-1 h-4 w-4 sm:mr-2 sm:h-5 sm:w-5" />
								<span className="hidden sm:inline">สถิติค่ายเกม</span>
								<span className="sm:hidden">ค่ายเกม</span>
							</TabsTrigger>
							<TabsTrigger
								value="referred"
								className="inline-flex min-w-max items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm sm:px-6 sm:py-3 sm:text-base"
							>
								<span className="hidden sm:inline">สมาชิกที่แนะนำ</span>
								<span className="sm:hidden">แนะนำ</span>
							</TabsTrigger>
							<TabsTrigger
								value="clearTurnoverHistory"
								className="inline-flex min-w-max items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm sm:px-6 sm:py-3 sm:text-base"
							>
								<span className="hidden sm:inline">ประวัติการเคลียเทิร์น</span>
								<span className="sm:hidden">เคลียเทิร์น</span>
							</TabsTrigger>
							<TabsTrigger
								value="activityLog"
								className="inline-flex min-w-max items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm sm:px-6 sm:py-3 sm:text-base"
							>
								<History className="mr-1 h-4 w-4 sm:mr-2 sm:h-5 sm:w-5" />
								<span className="hidden sm:inline">ประวัติการทำรายการ</span>
								<span className="sm:hidden">รายการ</span>
							</TabsTrigger>
						</TabsList>
					</div>

					<TabsContent value="transactions" className="mt-4 sm:mt-6">
						<TransactionTable playerId={id || ''} />
					</TabsContent>

					<TabsContent value="playlogs" className="mt-4 sm:mt-6">
						<PlaylogTable playerId={id || ''} />
					</TabsContent>

					<TabsContent value="providerStats" className="mt-4 sm:mt-6">
						<PlayerProviderStats playerId={id || ''} />
					</TabsContent>

					<TabsContent value="referred" className="mt-4 sm:mt-6">
						<ReferredPlayer playerId={id || ''} />
					</TabsContent>

					<TabsContent value="clearTurnoverHistory" className="mt-4 sm:mt-6">
						<PlayerClearTurnoverHistoryTable playerId={id || ''} />
					</TabsContent>

					<TabsContent value="activityLog" className="mt-4 sm:mt-6">
						<PlayerActivityLogTable playerId={id || ''} />
					</TabsContent>
				</Tabs>
			</div>
		</div>
	)
}
